import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Inbox,
  Settings,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { isLocalEnvironment } from './adminUtils';
import AdminLogin from './AdminLogin';

const NAV = [
  { to: '/admin', end: true, label: 'Overview', icon: LayoutDashboard },
  { to: '/admin/appointments', label: 'Appointments', icon: CalendarDays },
  { to: '/admin/registrations', label: 'Registrations', icon: Users },
  { to: '/admin/requests', label: 'Requests', icon: Inbox },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function AdminLayout({ children }) {
  const { token, authenticated, verifying, verifySession, logout, setAuthenticated } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(!!token);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      if (!token) {
        setBootstrapping(false);
        return;
      }
      if (authenticated) {
        setBootstrapping(false);
        return;
      }
      const ok = await verifySession(token);
      if (!cancelled) {
        if (!ok) setAuthenticated(false);
        setBootstrapping(false);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (bootstrapping || verifying) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(8,136,248,0.08), transparent 50%), #F7FAFD',
        }}
      >
        <div className="text-center">
          <div className="w-9 h-9 border-2 border-[#0888F8] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#6B7C90]">Loading…</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <AdminLogin />;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const NavItems = ({ onNavigate }) => (
    <nav className="flex-1 px-3 py-6 space-y-0.5">
      {NAV.map(({ to, end, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${
              isActive
                ? 'bg-[#0888F8] text-white shadow-sm shadow-[#0888F8]/25'
                : 'text-[#5A6B7D] hover:bg-[#0888F8]/[0.06] hover:text-[#1A2B3C]'
            }`
          }
        >
          <Icon size={17} strokeWidth={1.75} />
          {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div
      className="min-h-screen flex"
      style={{
        background:
          'radial-gradient(ellipse 60% 40% at 100% 0%, rgba(248,176,16,0.07), transparent 45%), radial-gradient(ellipse 50% 35% at 0% 100%, rgba(8,136,248,0.06), transparent 40%), #F7FAFD',
      }}
    >
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[220px] flex-col bg-white/70 backdrop-blur-md border-r border-[#E2EBF4] fixed inset-y-0 left-0 z-30">
        <div className="px-5 pt-6 pb-2">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="CardX" className="h-8 object-contain" />
            <div className="min-w-0">
              <p
                className="text-sm font-semibold text-[#1A2B3C] leading-tight truncate"
                style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
              >
                CardX
              </p>
              <p className="text-[11px] text-[#8A9BB0] tracking-wide">Admin</p>
            </div>
          </div>
        </div>
        <NavItems />
        <div className="p-3 pb-5">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-medium text-[#8A9BB0] hover:text-red-600 hover:bg-red-50/80 transition-colors"
          >
            <LogOut size={16} strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <button
            type="button"
            className="absolute inset-0 bg-[#1A2B3C]/25 backdrop-blur-[2px]"
            aria-label="Close menu"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white/95 backdrop-blur-md shadow-xl flex flex-col animate-in slide-in-from-left duration-200">
            <div className="px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="CardX" className="h-8 object-contain" />
                <span
                  className="font-semibold text-[#1A2B3C] text-sm"
                  style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                >
                  CardX Admin
                </span>
              </div>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-[#8A9BB0] hover:text-[#1A2B3C] rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
            <NavItems onNavigate={() => setSidebarOpen(false)} />
            <div className="p-3 pb-5">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-medium text-[#8A9BB0] hover:text-red-600"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-[220px] min-w-0">
        <header className="sticky top-0 z-20 bg-white/60 backdrop-blur-md border-b border-[#E2EBF4]/80">
          <div className="px-4 sm:px-8 py-3.5 flex items-center gap-3">
            <button
              type="button"
              className="lg:hidden p-2 rounded-xl text-[#5A6B7D] hover:bg-[#0888F8]/8 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={18} />
            </button>
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-semibold text-[#1A2B3C] truncate"
                style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
              >
                CardX Academia
              </p>
            </div>
            {isLocalEnvironment() ? (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium text-[#B45309] bg-[#F8B010]/15 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F8B010]" />
                Local
              </span>
            ) : (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0888F8] bg-[#0888F8]/8 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                Live
              </span>
            )}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-10 max-w-6xl">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
}
